import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  ChevronDown,
  FileText,
  Film,
  Image,
  Layers,
  Loader2,
  Sparkles,
  Upload,
  Users,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PromptAutocompleteTextarea } from '@/components/prompt/PromptAutocompleteTextarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { HelpHint } from '@/components/ui/help-hint'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { apiJson, apiFetch } from '@/lib/api'
import { useLocalStorageState } from '@/hooks/useLocalStorageState'
import type { Asset, ProductWithAssets } from '@/types'
import type { VideoMode } from '@/components/create/StepSetup'
import { VIDEO_MODE_CONFIG } from '@/components/create/videoModeConfig'
import { allocate } from '@/lib/sceneAllocator'

interface DraftScript {
  id: number
  title: string | null
  source_prompt: string
  status: string
  created_at: string
  versions: { version_number: number }[]
}

type ScriptProvider = 'gemini' | 'openrouter'

interface ScriptModelOption {
  id: ScriptProvider
  label: string
  model: string
  available: boolean
  pricing: { input_per_1m_usd: number; output_per_1m_usd: number }
}

interface ScriptModelsResponse {
  items: ScriptModelOption[]
  default: ScriptProvider
}

const SCRIPT_MODEL_STORAGE_KEY = 'aioverlords.scriptModel'

function _readStoredScriptModel(): ScriptProvider | null {
  try {
    const v = localStorage.getItem(SCRIPT_MODEL_STORAGE_KEY)
    return v === 'gemini' || v === 'openrouter' ? v : null
  } catch {
    return null
  }
}

function _writeStoredScriptModel(v: ScriptProvider): void {
  try {
    localStorage.setItem(SCRIPT_MODEL_STORAGE_KEY, v)
  } catch {
    /* localStorage may be disabled (private browsing) — silently ignore */
  }
}

/** The three video types supported by the Create Ad flow. */
export type VideoType = 'product' | 'avatar' | 'composite'

/** Map the user-visible Video style buttons to the backend style_profile id.
 *  Replaces the separate "Style profile" dropdown the client called out
 *  as a duplicate — the visible buttons drive both bias block AND profile. */
const VIDEO_MODE_TO_PROFILE: Record<VideoMode, string> = {
  ugc: 'ugc_authentic',
  cinematic_product: 'cinematic_high_product',
  lifestyle: 'editorial_lifestyle',
  hype_retail: 'hype_retail',
}

// Props kept backward-compatible — isQuickClip and onModeChange are ignored internally.
interface Props {
  isQuickClip: boolean
  onModeChange: (quick: boolean) => void
  onScriptCreated: (
    scriptId: number,
    quick: boolean,
    avatarMode?: boolean,
    avatarAssetId?: number | null,
    productId?: number | null,
    refImageIds?: number[],
    videoMode?: VideoMode,
  ) => void
  // Set when the user landed on /create with ?storyboard_asset_id=N AND the
  // attached asset has its structured plan persisted on Asset.meta. We
  // forward it on the script POST so the script generator emits panel-aware
  // per-scene Seedance prompts.
  storyboardPlan?: Record<string, unknown> | null
}

export function StepIdea({ onScriptCreated, storyboardPlan }: Props) {
  const navigate = useNavigate()

  // Core form state — persisted to localStorage so back-nav + page refresh
  // don't wipe the user's in-progress draft (Task 6: idea-stage persistence).
  // Cleared in one shot via clearCreateAdDraft() after a successful assembly.
  const [idea, setIdea] = useLocalStorageState('aioverlords.createAd.idea', '')
  const [duration, setDuration] = useLocalStorageState('aioverlords.createAd.duration', 15)
  const [sceneCount, setSceneCount] = useLocalStorageState('aioverlords.createAd.sceneCount', 1)
  const [notes, setNotes] = useLocalStorageState('aioverlords.createAd.notes', '')
  // Bridge toggle — when ON the assembly endpoint chains every scene to the
  // previous scene's last frame so identity, outfit, and setting stay locked.
  // Defaults OFF because most ads benefit from independent scenes.
  const [bridgeEnabled, setBridgeEnabled] = useLocalStorageState(
    'aioverlords.createAd.bridgeEnabled',
    false,
  )
  const [drafts, setDrafts] = React.useState<DraftScript[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Storyboard mode — when a storyboard is attached, default ON so the
  // script generator emits panel-aware per-scene prompts. User can flip
  // OFF to keep the storyboard as a visual ref only and let the script
  // generator auto-plan scenes from the source prompt. Physics rules
  // (negative prompts, PHYSICS DISCIPLINE block, visible-effect
  // enforcement) apply regardless — they live in the script template
  // and are NOT gated on this toggle.
  const [storyboardModeOn, setStoryboardModeOn] = useLocalStorageState(
    'aioverlords.createAd.storyboardModeOn',
    true,
  )
  // Storyboard plan plumbing lives further down, after selectedRefAssets is
  // declared (it's the only state we depend on).

  // Script model picker — sticks across page reloads via localStorage so a user
  // who picked OpenRouter once doesn't lose it on refresh. Falls back to the
  // API's reported default when nothing is stored locally.
  const [scriptModel, setScriptModel] = React.useState<ScriptProvider>(
    () => _readStoredScriptModel() ?? 'gemini',
  )
  const [scriptModels, setScriptModels] = React.useState<ScriptModelOption[]>([])

  const handleScriptModelChange = (value: ScriptProvider) => {
    setScriptModel(value)
    _writeStoredScriptModel(value)
  }

  // Three-way video type toggle — persisted across back-nav + refresh.
  const [videoType, setVideoType] = useLocalStorageState<VideoType>(
    'aioverlords.createAd.videoType',
    'product',
  )

  // Video style mode — used for avatar mode (single global picker).
  // For product/composite, per-scene styles in sceneStyles[] take over.
  // Persisted alongside the other idea-stage fields.
  const [videoMode, setVideoMode] = useLocalStorageState<VideoMode>(
    'aioverlords.createAd.videoMode',
    'cinematic_product',
  )

  // Per-scene styles — one entry per scene, parallel to sceneCount.
  // Only used when videoType ∈ {product, composite}.
  const [sceneStyles, setSceneStyles] = useLocalStorageState<VideoMode[]>(
    'aioverlords.createAd.sceneStyles',
    ['cinematic_product'],
  )

  // Per-scene durations — each must be 5, 10, or 15 (Seedance constraint).
  const [sceneDurations, setSceneDurations] = useLocalStorageState<number[]>(
    'aioverlords.createAd.sceneDurations',
    [5],
  )

  // Active tab in the per-scene style picker panel.
  const [activeSceneTab, setActiveSceneTab] = React.useState(0)

  // Whether the per-scene styles panel is expanded. Default open when
  // sceneCount > 1, collapsed when = 1. Sync'd with sceneCount changes.
  const [sceneStylesOpen, setSceneStylesOpen] = React.useState(sceneCount > 1)

  // Keep default in sync when video type changes (only if user has not manually changed it)
  const videoModeAutoRef = React.useRef(true)
  const handleVideoTypeChange = (type: VideoType) => {
    setVideoType(type)
    if (videoModeAutoRef.current) {
      setVideoMode(type === 'avatar' ? 'ugc' : 'cinematic_product')
    }
  }

  // Reconcile sceneStyles and sceneDurations with sceneCount.
  // Q3: preserve min(old, new) entries; pad new entries with last existing style.
  // Q4: when sceneCount changes, recompute durations via allocator.
  const handleSceneCountChange = (newCount: number) => {
    setSceneCount(newCount)
    // Update styles: preserve overlap, pad with last style
    setSceneStyles((prev) => {
      const lastStyle = prev.length > 0 ? prev[prev.length - 1] : ('cinematic_product' as VideoMode)
      if (newCount <= prev.length) return prev.slice(0, newCount)
      const extra = Array(newCount - prev.length).fill(lastStyle) as VideoMode[]
      return [...prev, ...extra]
    })
    // Recompute durations via allocator — total = sum of current sceneDurations
    setSceneDurations((prev) => {
      const currentTotal = prev.reduce((s, d) => s + d, 0)
      // Ensure total is divisible by 5 and at least 5*newCount
      const safeTotal = Math.max(
        Math.round(currentTotal / 5) * 5,
        5 * newCount,
      )
      // Also cap: total <= 15 * newCount
      const cappedTotal = Math.min(safeTotal, 15 * newCount)
      const aligned = Math.round(cappedTotal / 5) * 5
      try {
        return allocate({ total: aligned, sceneCount: newCount }).durations
      } catch {
        // Fallback: all-5s
        return Array(newCount).fill(5) as number[]
      }
    })
    // Auto-expand panel when going to multiple scenes
    setSceneStylesOpen(newCount > 1)
    // Clamp active tab to valid range
    setActiveSceneTab((t) => Math.min(t, newCount - 1))
  }

  // Recompute sceneDurations when `duration` (the total) changes.
  const handleTotalDurationChange = (newTotal: number) => {
    setDuration(newTotal)
    const safeTotal = Math.max(Math.round(newTotal / 5) * 5, 5)
    // Try with the current sceneCount; if infeasible, let allocator pick a count.
    try {
      const result = allocate({ total: safeTotal, sceneCount })
      setSceneDurations(result.durations)
    } catch {
      // Current sceneCount is incompatible with the new total; let the
      // allocator pick the smallest scene count that works.
      try {
        const result = allocate({ total: safeTotal })
        setSceneDurations(result.durations)
        const newCount = result.durations.length
        setSceneCount(newCount)
        setSceneStyles((prev) => {
          const lastStyle = prev.length > 0 ? prev[prev.length - 1] : ('cinematic_product' as VideoMode)
          if (newCount <= prev.length) return prev.slice(0, newCount)
          const extra = Array(newCount - prev.length).fill(lastStyle) as VideoMode[]
          return [...prev, ...extra]
        })
        setSceneStylesOpen(newCount > 1)
        setActiveSceneTab((t) => Math.min(t, newCount - 1))
      } catch {
        // If still fails (e.g. total not divisible by 5), keep current durations
      }
    }
  }

  // Update a single scene's duration and recompute total displayed.
  const handleSceneDurationChange = (sceneIdx: number, newDur: number) => {
    setSceneDurations((prev) => {
      const next = [...prev]
      next[sceneIdx] = newDur
      // Sync total duration to the new sum
      const newTotal = next.reduce((s, d) => s + d, 0)
      setDuration(newTotal)
      return next
    })
  }

  // Product picker state (product + composite modes) — only the selected
  // product ID is persisted; the products[] catalog refetches on mount.
  const [products, setProducts] = React.useState<ProductWithAssets[]>([])
  const [productsLoading, setProductsLoading] = React.useState(false)
  const [selectedProductId, setSelectedProductId] = useLocalStorageState<number | null>(
    'aioverlords.createAd.productId',
    null,
  )

  // Unified reference image state — works for all three video types.
  // Seedance accepts up to 9 image_urls per call (assembly_tasks enforces
  // [:9] before submission); we expose the same cap here so users can pass
  // multi-angle product + lifestyle + character refs in a single ad.
  //
  // Persistence: only the asset IDs are written to localStorage (full Asset
  // objects don't round-trip cleanly). On mount we rehydrate the Asset[]
  // by re-fetching each ID via apiJson. See refIdsHydrationRef below.
  const [selectedRefAssets, setSelectedRefAssets] = React.useState<Asset[]>([])
  const [selectedRefAssetIds, setSelectedRefAssetIds] = useLocalStorageState<number[]>(
    'aioverlords.createAd.refAssetIds',
    [],
  )

  // Hydration: on first mount, if localStorage held ref asset IDs but
  // selectedRefAssets is empty (fresh remount or page refresh), fetch each
  // Asset and populate the full array so the picker UI shows what the user
  // had before. Skipped when the IDs are already represented in state
  // (back-nav within the same session — assets are still in memory).
  const refIdsHydratedRef = React.useRef(false)
  React.useEffect(() => {
    if (refIdsHydratedRef.current) return
    if (selectedRefAssetIds.length === 0 || selectedRefAssets.length > 0) {
      refIdsHydratedRef.current = true
      return
    }
    let cancelled = false
    ;(async () => {
      const fetched: Asset[] = []
      for (const id of selectedRefAssetIds) {
        try {
          const a = await apiJson<Asset>(`/api/v1/assets/${id}`)
          fetched.push(a)
        } catch {
          /* asset deleted or unreachable — drop it from rehydration */
        }
      }
      if (cancelled) return
      if (fetched.length > 0) {
        setSelectedRefAssets(fetched)
        // Prune the persisted ID list down to what actually resolved so a
        // partial-404 (1 of 3 IDs deleted) doesn't leave stale IDs sitting
        // in localStorage waiting to be re-fetched next mount.
        setSelectedRefAssetIds(fetched.map((a) => a.id))
      } else {
        // Every stored ID is stale — wipe the cache so we don't retry next mount
        setSelectedRefAssetIds([])
      }
      refIdsHydratedRef.current = true
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep persisted ref-asset IDs in sync with the in-memory Asset[]. This
  // runs after addRefAsset/removeRefAsset/toggleRefAsset mutate the array.
  // Only writes when the ID list actually differs to avoid React loops.
  //
  // The refIdsHydratedRef guard prevents this effect from racing the
  // hydration effect above on mount: if hydration is still fetching the
  // Asset objects, selectedRefAssets is still [] but selectedRefAssetIds
  // is non-empty — running this effect prematurely would overwrite the
  // persisted IDs to []. Hydration sets refIdsHydratedRef.current = true
  // when it finishes; only then can the sync effect take over.
  React.useEffect(() => {
    if (!refIdsHydratedRef.current) return
    const nextIds = selectedRefAssets.map((a) => a.id)
    const same =
      nextIds.length === selectedRefAssetIds.length &&
      nextIds.every((id, i) => id === selectedRefAssetIds[i])
    if (!same) setSelectedRefAssetIds(nextIds)
    // selectedRefAssetIds intentionally omitted — we drive it FROM assets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRefAssets])
  const [showRefLibrary, setShowRefLibrary] = React.useState(false)
  const [refImageUploading, setRefImageUploading] = React.useState(false)

  // Seedance accepts up to 9 image refs. When bridge ("same person across
  // scenes") is on, every scene N>=2 has its predecessor's last frame
  // prepended as a continuity anchor, consuming one ref slot — so the user
  // cap drops to 8. Keeping it at 9 would force the worker to silently drop
  // the last product image on bridged scenes, breaking product/outfit
  // consistency across cuts (the exact thing bridge is meant to preserve).
  const MAX_REF_IMAGES = bridgeEnabled ? 8 : 9

  // Track structured storyboard plans loaded from library-picked refs.
  // Keyed by asset_id so we can drop them when the user removes the ref.
  // The CreateAd-passed `storyboardPlan` prop covers the "Use in Create Ad"
  // handoff path (URL ?storyboard_asset_id=N); this state covers the case
  // where the user picks a storyboard from the library / saved-context.
  const [libraryStoryboardPlans, setLibraryStoryboardPlans] = React.useState<
    Record<number, Record<string, unknown>>
  >({})

  // The plan we forward to script_service: prefer the URL-handoff plan if
  // present, else the first storyboard ref the user picked from library.
  const effectiveStoryboardPlan: Record<string, unknown> | null = React.useMemo(() => {
    if (storyboardPlan) return storyboardPlan
    const firstStoryboardId = selectedRefAssets.find(
      (a) => (a.scene_type ?? '').toLowerCase() === 'storyboard',
    )?.id
    if (firstStoryboardId && libraryStoryboardPlans[firstStoryboardId]) {
      return libraryStoryboardPlans[firstStoryboardId]
    }
    return null
  }, [storyboardPlan, selectedRefAssets, libraryStoryboardPlans])

  // Lazy-load the storyboard plan for any storyboard ref the user picked
  // from the library (not via the URL handoff). One fetch per asset_id;
  // cached on libraryStoryboardPlans so swapping refs is cheap.
  React.useEffect(() => {
    const storyboardIds = selectedRefAssets
      .filter((a) => (a.scene_type ?? '').toLowerCase() === 'storyboard')
      .map((a) => a.id)
      .filter((id) => !(id in libraryStoryboardPlans))
    if (storyboardIds.length === 0) return
    let cancelled = false
    ;(async () => {
      const fetched: Record<number, Record<string, unknown>> = {}
      for (const id of storyboardIds) {
        try {
          const asset = await apiJson<{ meta?: { storyboard_plan?: Record<string, unknown> } | null }>(
            `/api/v1/assets/${id}`,
          )
          const plan = asset?.meta?.storyboard_plan
          if (plan && typeof plan === 'object') fetched[id] = plan
        } catch {
          /* skip unreachable asset */
        }
      }
      if (cancelled || Object.keys(fetched).length === 0) return
      setLibraryStoryboardPlans((prev) => ({ ...prev, ...fetched }))
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRefAssets.map((a) => `${a.id}:${a.scene_type}`).join(',')])

  // Auto-fill the source prompt with the storyboard's brief on first
  // render — saves the user from re-typing what they already described
  // in the Storyboard tab. We only fill when the textarea is empty so
  // we don't clobber edits.
  const storyboardBriefAutoFilledRef = React.useRef(false)
  React.useEffect(() => {
    if (storyboardBriefAutoFilledRef.current) return
    if (!effectiveStoryboardPlan) return
    const meta = (effectiveStoryboardPlan as { meta?: { brief?: string } }).meta
    const brief = meta?.brief
    if (typeof brief === 'string' && brief.length > 0 && idea.trim().length === 0) {
      setIdea(brief)
      storyboardBriefAutoFilledRef.current = true
    }
    // intentionally exhaustive on effectiveStoryboardPlan only
  }, [effectiveStoryboardPlan]) // eslint-disable-line react-hooks/exhaustive-deps

  // Pulse animation: when a ref is added, briefly highlight it so the user
  // notices the upload landed (especially after upload-via-button which
  // doesn't flash naturally).
  const [pulseRefId, setPulseRefId] = React.useState<number | null>(null)

  const addRefAsset = (asset: Asset) => {
    setSelectedRefAssets((prev) => {
      if (prev.some((a) => a.id === asset.id)) return prev
      if (prev.length >= MAX_REF_IMAGES) return prev
      return [...prev, asset]
    })
    setPulseRefId(asset.id)
    window.setTimeout(() => {
      setPulseRefId((current) => (current === asset.id ? null : current))
    }, 1600)
  }

  const removeRefAsset = (assetId: number) => {
    setSelectedRefAssets((prev) => prev.filter((a) => a.id !== assetId))
  }

  const toggleRefAsset = (asset: Asset) => {
    const alreadySelected = selectedRefAssets.some((a) => a.id === asset.id)
    if (alreadySelected) {
      removeRefAsset(asset.id)
    } else if (selectedRefAssets.length < MAX_REF_IMAGES) {
      addRefAsset(asset)
    }
    // If at max and not already selected, do nothing (UI disables the button)
  }

  /** Click-to-insert: append "@Image1" / "@Image2" / etc. to the prompt. */
  const insertImageTag = (idx: number) => {
    const tag = `@Image${idx + 1}`
    setIdea((current) => {
      const trimmed = current.trimEnd()
      // Don't double-append if it's already the last token.
      if (trimmed.endsWith(tag)) return current
      const sep = trimmed.length === 0 ? '' : ' '
      return `${trimmed}${sep}${tag} `
    })
  }

  /** Refresh a single asset's analysis status — called by the poll effect
   *  below so the "analyzing…" badge clears and ai_description becomes
   *  available without a manual reload. */
  const refreshRefAsset = React.useCallback(async (assetId: number) => {
    try {
      const fresh = await apiJson<Asset>(`/api/v1/assets/${assetId}`)
      setSelectedRefAssets((prev) =>
        prev.map((a) => (a.id === assetId ? fresh : a)),
      )
    } catch {
      /* silent — keep stale entry, will retry next poll */
    }
  }, [])

  // Poll pending-analysis refs every 5s. Stops automatically once all are
  // analysed (or the user removes them).
  React.useEffect(() => {
    const pendingIds = selectedRefAssets
      .filter((a) => (a.analysis_status ?? 'completed') === 'pending')
      .map((a) => a.id)
    if (pendingIds.length === 0) return
    const handle = window.setInterval(() => {
      pendingIds.forEach(refreshRefAsset)
    }, 5000)
    return () => window.clearInterval(handle)
    // selectedRefAssets shape changes drive this effect; refreshRefAsset is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRefAssets.map((a) => `${a.id}:${a.analysis_status}`).join(',')])

  // Shared library data (used by the reference image picker)
  const [libraryAssets, setLibraryAssets] = React.useState<Asset[]>([])
  const [libraryLoading, setLibraryLoading] = React.useState(false)
  const [libraryFolders, setLibraryFolders] = React.useState<{ id: number; name: string }[]>([])
  const [selectedFolderId, setSelectedFolderId] = React.useState<number | null>(null)
  const [sortBy, setSortBy] = React.useState<'newest' | 'oldest' | 'name'>('newest')

  // Derived
  const needsProduct = videoType === 'product'
  const needsAvatar = videoType === 'avatar' || videoType === 'composite'
  // isAvatar = any mode that generates an avatar/talking-head
  const isAvatar = needsAvatar
  const selectedProduct = products.find((p) => p.id === selectedProductId) ?? null
  // Images linked to the selected product (from product KB)
  const productImages: Asset[] = selectedProduct?.assets
    ?.map((a) => a.asset)
    .filter((a): a is Asset => !!a && a.file_type === 'image') ?? []

  // Load drafts, products, and script-model catalog on mount.
  // Style profiles aren't fetched here anymore — the visible Video style
  // buttons drive the backend style_profile via VIDEO_MODE_TO_PROFILE.
  React.useEffect(() => {
    apiJson<{ items: DraftScript[] }>('/api/v1/scripts?limit=5')
      .then((data) => setDrafts(data.items.filter((s) => s.status !== 'archived')))
      .catch(() => setDrafts([]))

    apiJson<ScriptModelsResponse>('/api/v1/scripts/models')
      .then((data) => {
        setScriptModels(data.items)
        // If the user has nothing stored AND the API has a different default,
        // honour the API. Don't override an explicit user choice.
        const stored = _readStoredScriptModel()
        if (!stored && data.default && data.default !== scriptModel) {
          setScriptModel(data.default)
        }
      })
      .catch(() => setScriptModels([]))

    setProductsLoading(true)
    apiJson<{ items: ProductWithAssets[] }>('/api/v1/products')
      .then((data) => setProducts(data.items))
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close library picker when switching video type; keep selected images
  React.useEffect(() => {
    if (!needsProduct) setSelectedProductId(null)
    setShowRefLibrary(false)
  }, [videoType]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-bind ALL of a product's linked images when a product is selected.
  // Order by role priority so Seedance gets the most useful refs first when
  // the gallery is bigger than MAX_REF_IMAGES:
  //   hero_image → lifestyle → texture → packaging → anything else
  // Capped at MAX_REF_IMAGES (Seedance hard cap = 9). We only auto-add when
  // the user hasn't already added refs manually — never overwrite their
  // explicit choices when they switch products.
  React.useEffect(() => {
    if (!selectedProductId) return
    if (selectedRefAssets.length > 0) return
    const product = products.find((p) => p.id === selectedProductId)
    if (!product?.assets || product.assets.length === 0) return

    const ROLE_PRIORITY: Record<string, number> = {
      hero_image: 0,
      lifestyle: 1,
      texture: 2,
      packaging: 3,
    }
    const ordered = [...product.assets]
      .filter((pa) => pa.asset?.file_type === 'image')
      .sort((a, b) => {
        const pa = ROLE_PRIORITY[a.role] ?? 99
        const pb = ROLE_PRIORITY[b.role] ?? 99
        return pa - pb
      })
      .slice(0, MAX_REF_IMAGES)

    ordered.forEach((pa) => {
      if (pa.asset) addRefAsset(pa.asset)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, products])

  const loadLibraryData = (force: boolean = false) => {
    // Refetch on every explicit open — the user could have just saved a
    // new asset on /create-image, so a session-scoped cache silently hides
    // their newly-saved images. We only skip the fetch on the first
    // initial-mount auto-load (force=false + already populated).
    if (!force && libraryAssets.length > 0) return
    setLibraryLoading(true)
    Promise.all([
      apiJson<{ items: Asset[] }>('/api/v1/assets/search?limit=200').catch(() => ({ items: [] })),
      apiJson<{ items: { id: number; name: string }[] }>('/api/v1/folders').catch(() => ({ items: [] })),
    ]).then(([assetsData, foldersData]) => {
      setLibraryAssets(assetsData.items.filter((a) => a.file_type === 'image'))
      setLibraryFolders(foldersData.items)
    }).finally(() => setLibraryLoading(false))
  }

  const handleOpenRefLibrary = () => {
    setShowRefLibrary((prev) => !prev)
    // Always refetch when opening the picker so newly-saved assets show up.
    if (!showRefLibrary) loadLibraryData(true)
  }

  // Upload one or more images directly and add them to the selected reference
  // assets. Multi-select is supported on the file input; we upload in
  // parallel and surface a single error if any failed.
  const handleRefUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList)

    // Cap the number we'll accept based on the remaining slots.
    const remaining = MAX_REF_IMAGES - selectedRefAssets.length
    if (remaining <= 0) {
      setError(`Maximum ${MAX_REF_IMAGES} reference images allowed. Remove one to upload another.`)
      e.target.value = ''
      return
    }
    const accepted = files.slice(0, remaining)
    const skipped = files.length - accepted.length

    setRefImageUploading(true)
    setError(null)
    try {
      const results = await Promise.allSettled(
        accepted.map(async (file) => {
          const formData = new FormData()
          formData.append('file', file)
          const res = await apiFetch('/api/v1/assets/quick-save', {
            method: 'POST',
            body: formData,
          })
          if (!res.ok) throw new Error(`upload failed: ${file.name}`)
          return (await res.json()) as Asset
        }),
      )

      const successes: Asset[] = []
      let failedCount = 0
      results.forEach((r) => {
        if (r.status === 'fulfilled') successes.push(r.value)
        else failedCount += 1
      })

      successes.forEach((asset) => {
        addRefAsset(asset)
      })
      if (successes.length) {
        // Prepend in upload order so the picker shows newest first.
        setLibraryAssets((prev) => [...successes, ...prev])
      }

      const messages: string[] = []
      if (failedCount > 0) messages.push(`${failedCount} upload${failedCount === 1 ? '' : 's'} failed`)
      if (skipped > 0) messages.push(`${skipped} skipped (max ${MAX_REF_IMAGES})`)
      if (messages.length) setError(messages.join(' — '))
    } catch {
      setError('Image upload failed.')
    } finally {
      setRefImageUploading(false)
      e.target.value = ''
    }
  }

  // Folder + sort filtered ref assets
  const filteredRefAssets = React.useMemo(() => {
    let filtered = libraryAssets
    if (selectedFolderId !== null) {
      filtered = filtered.filter((a) => a.folder_id === selectedFolderId)
    }
    const sorted = [...filtered]
    if (sortBy === 'newest') sorted.sort((a, b) => b.created_at.localeCompare(a.created_at))
    else if (sortBy === 'oldest') sorted.sort((a, b) => a.created_at.localeCompare(b.created_at))
    else sorted.sort((a, b) => (a.original_filename ?? a.filename).localeCompare(b.original_filename ?? b.filename))
    return sorted
  }, [libraryAssets, selectedFolderId, sortBy])

  const handleSubmit = async () => {
    if (!idea.trim()) return
    setLoading(true)
    setError(null)
    try {
      const primaryRef = selectedRefAssets[0] ?? null
      const avatarDesc =
        isAvatar && primaryRef
          ? [primaryRef.ai_description, primaryRef.ai_tags?.join(', ')]
              .filter(Boolean)
              .join('. ') || 'avatar spokesperson'
          : undefined

      let notesWithProduct = notes.trim()
      if (selectedProduct) {
        const parts = [
          `PRODUCT: ${selectedProduct.name}.`,
          selectedProduct.benefits?.length
            ? `Benefits: ${selectedProduct.benefits.join(', ')}.`
            : null,
          `Brand voice: ${selectedProduct.brand_voice || 'premium, natural'}.`,
          selectedProduct.price ? `Price: ${selectedProduct.price}.` : null,
        ].filter(Boolean)
        notesWithProduct = [notesWithProduct, parts.join(' ')].filter(Boolean).join('\n\n')
      }

      // Build the reference_assets payload — one entry per selected ref,
      // tagged @Image1/@Image2/@Image3 in the order shown in the UI. The
      // backend renders these into the script-gen prompt so the LLM places
      // the right tag on the right scene; the downstream Seedance pipeline
      // then binds each tag to the actual image at video-gen time.
      //
      //  - asset_id   → resume hydration after hard refresh
      //  - kind       → structural intent (e.g. "storyboard") so the
      //                 script generator branches into panel-aware mode
      //                 without waiting for the asset-analysis task to
      //                 populate ai_description
      const referenceAssets = selectedRefAssets.map((a, idx) => ({
        asset_id: a.id,
        tag: `@Image${idx + 1}`,
        description: a.ai_description ?? null,
        tags: a.ai_tags ?? [],
        kind: a.scene_type ?? null,
      }))

      // For product/composite: send per-scene modes + durations.
      // For avatar: send the legacy single video_mode (backend handles both).
      const isPerScene = videoType === 'product' || videoType === 'composite'
      const perSceneModes = isPerScene ? sceneStyles.slice(0, sceneCount) : undefined
      const perSceneDurations = isPerScene ? sceneDurations.slice(0, sceneCount) : undefined
      // Derive style_profile from the first scene's mode (or global for avatar)
      const effectiveMode = isPerScene
        ? (perSceneModes?.[0] ?? 'cinematic_product')
        : videoMode

      const data = await apiJson<{ id: number }>('/api/v1/scripts', {
        method: 'POST',
        body: JSON.stringify({
          source_prompt: idea.trim(),
          duration,
          scene_count: sceneCount,
          // Derived from the effective mode — per-scene or global.
          style_profile: VIDEO_MODE_TO_PROFILE[effectiveMode],
          notes: notesWithProduct || undefined,
          avatar_mode: isAvatar,
          avatar_description: avatarDesc,
          // For avatar: single global mode. For product/composite: first scene's
          // mode (backend uses scene_video_modes for per-scene assignment).
          video_mode: effectiveMode,
          model_provider: scriptModel,
          reference_assets: referenceAssets,
          // Per-scene overrides — only sent for product/composite.
          ...(perSceneModes ? { scene_video_modes: perSceneModes } : {}),
          ...(perSceneDurations ? { scene_durations: perSceneDurations } : {}),
          // When storyboard mode is ON, forward the structured plan so the
          // script generator emits panel-aware per-scene Seedance prompts.
          // When OFF, the storyboard image is still attached as @Image1 (a
          // visual reference) but the LLM auto-plans scenes from the
          // source prompt instead of locking to panel beats. Either way,
          // physics rules in the script template still apply.
          // effectiveStoryboardPlan covers BOTH paths: URL handoff
          // (?storyboard_asset_id=N) AND library-picked storyboard refs.
          ...(effectiveStoryboardPlan && storyboardModeOn
            ? { storyboard_plan: effectiveStoryboardPlan }
            : {}),
          // Bridge toggle — forward to script POST so the backend can annotate
          // the script with continuity intent; StepGenerate forwards it again
          // to the assembly endpoint.
          bridge_enabled: bridgeEnabled,
        }),
      })

      // Pass first image as refImageId (back-compat) and full array as refImageIds.
      const refImageId = selectedRefAssets[0]?.id ?? null
      const refImageIds = selectedRefAssets.map((a) => a.id)
      // For avatar: carry global videoMode. For product/composite: carry first scene's mode.
      const passedMode = isPerScene ? (perSceneModes?.[0] ?? 'cinematic_product') : videoMode
      onScriptCreated(data.id, false, isAvatar, refImageId, selectedProductId, refImageIds, passedMode)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const videoTypeLabels: Record<VideoType, string> = {
    product: `Product video — attach up to ${MAX_REF_IMAGES} reference images (front / 3-4 angle / lifestyle) so the model has consistent visuals to anchor each scene.`,
    avatar: 'Talking-head video — attach a person image; the script becomes spoken dialogue lip-synced to the avatar.',
    composite: 'Product + avatar together — a person presenting / using the product in the same shot. Attach both a person image and a product image so the model can render them interacting naturally.',
  }

  // Per-button tooltip copy for the videoType picker (Task 9). The body
  // text below the picker shows the description for the SELECTED mode;
  // these tooltips fire on hover so users compare options without clicking.
  const videoTypeTooltips: Record<VideoType, string> = {
    product: 'Product-only scenes. No person on camera. Best for hero shots, demos, before/after, environment-with-product. Multi-angle product refs improve consistency.',
    avatar: 'Talking-head / spokesperson video. A person speaks directly to camera; the script becomes spoken dialogue, lip-synced via OmniHuman or HeyGen. No product needed.',
    composite: 'Product + avatar in the same shot. A person holds, uses, or presents the product. Attach both refs — model renders them interacting. Best for UGC reviews, demos, before/after testimonials.',
  }

  const canGenerate =
    idea.trim().length > 0 &&
    !loading &&
    !refImageUploading &&
    // Bridge reserves one ref slot for the continuity frame (cap = 8). Block
    // submit when over the effective cap so the user trims here rather than
    // hitting the endpoint's 422 (or the worker silently dropping a ref).
    selectedRefAssets.length <= MAX_REF_IMAGES &&
    (videoType === 'product' || selectedRefAssets.length > 0)

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header — the drafts link tucks into the right-hand side, collapsed
           by default. Client called the always-on "5 drafts available"
           banner noisy. */}
      {drafts.length > 0 ? (
        <details className="group">
          <summary className="cursor-pointer flex items-start justify-between gap-3 list-none">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold">Create your ad</h2>
              <p className="text-sm text-muted-foreground">
                Describe your concept in plain English — the AI will write a full script.
              </p>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground/80 hover:text-foreground transition-colors flex items-center gap-1 py-1">
              <FileText className="h-3 w-3" />
              Resume a draft
              <span className="opacity-60">({drafts.length})</span>
            </span>
          </summary>
          <Card className="border-dashed mt-3">
            <CardContent className="px-4 pb-3 pt-3 space-y-2">
              {drafts.map((d) => {
                const resumeStep = d.status === 'approved' ? 3 : 2
                const label = d.status === 'approved' ? 'Setup' : 'Edit script'
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => navigate(`/create?step=${resumeStep}&script_id=${d.id}`)}
                    className="w-full flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {d.title || d.source_prompt.slice(0, 60)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(d.created_at).toLocaleDateString()} · v{d.versions.length}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge
                        variant={d.status === 'approved' ? 'default' : 'outline'}
                        className="text-[10px]"
                      >
                        {d.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        {label} <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </button>
                )
              })}
            </CardContent>
          </Card>
        </details>
      ) : (
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">Create your ad</h2>
          <p className="text-sm text-muted-foreground">
            Describe your concept in plain English — the AI will write a full script.
          </p>
        </div>
      )}

      {/* Storyboard mode banner — visible only when a storyboard is attached.
           Default ON: the script generator uses the structured plan to emit
           panel-aware per-scene Seedance prompts. OFF: the storyboard image
           still lands as @Image1 reference but scenes auto-plan from the
           source prompt. Physics rules apply in both cases. */}
      {effectiveStoryboardPlan && (
        <div className="rounded-md border border-foreground/30 bg-foreground/5 p-3 space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5 min-w-0">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                Storyboard mode
                <span className={cn(
                  'text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5',
                  storyboardModeOn
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground border border-border',
                )}>
                  {storyboardModeOn ? 'on' : 'off'}
                </span>
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {storyboardModeOn ? (
                  <>
                    Each scene's prompt will match the corresponding storyboard panel
                    (subject, beat, palette, lighting). The image is bound as a
                    multi-panel reference, not a product photo.
                  </>
                ) : (
                  <>
                    The storyboard image is attached as a visual reference only.
                    Scenes will be auto-planned from your source prompt below.
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setStoryboardModeOn((on) => !on)}
              className={cn(
                'shrink-0 text-xs px-3 py-1 rounded-md border transition-colors',
                storyboardModeOn
                  ? 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/50'
                  : 'border-foreground bg-foreground text-background',
              )}
            >
              {storyboardModeOn ? 'Turn off' : 'Turn on'}
            </button>
          </div>
        </div>
      )}

      {/* Video type + reference image */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-1.5">
            What type of video?
            <HelpHint text="Product: scenes showcasing the product, no person on camera. Avatar: a person speaks directly to camera (lip-synced). Product + Avatar: a person presents or uses the product in the same shot. Hover each button for the full description." />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Three-way toggle — per-button hover tooltips so users compare
              options without clicking through (Task 9). */}
          <TooltipProvider delayDuration={250}>
            <div
              className="inline-flex items-center rounded-lg border border-border bg-muted p-1 gap-1 flex-wrap"
              role="group"
              aria-label="Video type"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleVideoTypeChange('product')}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                      videoType === 'product'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    aria-pressed={videoType === 'product'}
                  >
                    <Film className="h-3.5 w-3.5" />
                    Product Video
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                  {videoTypeTooltips.product}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleVideoTypeChange('avatar')}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                      videoType === 'avatar'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    aria-pressed={videoType === 'avatar'}
                  >
                    <Users className="h-3.5 w-3.5" />
                    Talking head / Avatar
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                  {videoTypeTooltips.avatar}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleVideoTypeChange('composite')}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                      videoType === 'composite'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    aria-pressed={videoType === 'composite'}
                  >
                    <Layers className="h-3.5 w-3.5" />
                    Product + Avatar
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                  {videoTypeTooltips.composite}
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          <p className="text-xs text-muted-foreground">{videoTypeLabels[videoType]}</p>

          {/* Video style picker was removed from Step 1 (QA Round 1) — it
              lives on Step 3 (Setup) only now, since "production setup" is
              the right place for that decision. videoMode state still lives
              here, auto-defaults via handleVideoTypeChange (UGC for avatar,
              Cinematic for product/composite), and propagates to Setup via
              onScriptCreated. The user can change it on Setup. */}

          {/* ── Product dropdown (product + composite modes) ── */}
          {needsProduct && (
            <div className="border-t border-border pt-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Product (optional)</p>
              {productsLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading products...
                </div>
              ) : products.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No products yet.{' '}
                  <a href="/products" target="_blank" rel="noreferrer" className="underline hover:text-foreground">
                    Add a product
                  </a>
                </p>
              ) : (
                <Select
                  value={selectedProductId !== null ? String(selectedProductId) : '__none__'}
                  onValueChange={(v) => setSelectedProductId(v === '__none__' ? null : Number(v))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a product..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-muted-foreground">None</span>
                    </SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                        {p.price ? ` — ${p.price}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {selectedProduct && (
                <div className="flex items-center gap-3 rounded-md border border-border p-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{selectedProduct.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[selectedProduct.category, selectedProduct.price].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedProductId(null)}
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label="Remove product selection"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Reference images — universal for ALL video types (Seedance cap: 9) ── */}
          <div className="border-t border-border pt-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Image className="h-3 w-3" />
              Product reference images (up to {MAX_REF_IMAGES})
              <HelpHint text={`Up to ${MAX_REF_IMAGES} reference images. Mix angles (front, 3/4, label close-up), lifestyle settings, and character refs — the AI uses them to keep the product visually consistent across every scene. More consistent angles dramatically improve label and product accuracy across frames. NOTE: Seedance renders label text as decorative texture, not readable copy. If your product has critical brand text or pricing, add it as a Text Overlay after the video generates — that gives you pixel-perfect, legible burn-in.`} side="right" />
              {needsAvatar ? (
                <span className="text-foreground/80">*</span>
              ) : (
                <span className="text-muted-foreground/60 font-normal">(optional)</span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground/80">
              {needsAvatar
                ? 'Select the avatar / composite image the model will animate.'
                : 'Front, 3/4 angle, label close-up. More consistent angles = better label accuracy.'}
            </p>

            {/* Selected images — labelled, taggable, analysis-aware */}
            {selectedRefAssets.length > 0 && (
              <div className="flex flex-wrap gap-3 items-start">
                {selectedRefAssets.map((asset, idx) => {
                  const status = asset.analysis_status ?? 'completed'
                  const analyzing = status === 'pending'
                  const isPulsing = pulseRefId === asset.id
                  const isStoryboard = (asset.scene_type ?? '').toLowerCase() === 'storyboard'
                  return (
                    <div key={asset.id} className="flex flex-col gap-1.5 items-center">
                      <div
                        className={cn(
                          'relative w-20 h-20 rounded-md overflow-hidden border-2 shrink-0 transition-all',
                          isPulsing
                            ? 'border-foreground ring-2 ring-foreground/40 animate-pulse'
                            : 'border-foreground/40',
                        )}
                        title={asset.ai_description ?? asset.original_filename ?? asset.filename}
                      >
                        <img
                          src={`/uploads/${asset.file_path}`}
                          alt={asset.original_filename ?? asset.filename}
                          className="w-full h-full object-cover"
                        />
                        {/* Storyboard badge — top-left of the thumbnail when
                             this ref is a multi-panel storyboard. Tells the
                             user the script generator will treat this as a
                             layout map, not a product photo. */}
                        {isStoryboard && (
                          <span className="absolute top-1 left-1 text-[8px] font-semibold uppercase tracking-wide bg-foreground text-background px-1 py-0.5 rounded">
                            Storyboard
                          </span>
                        )}
                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => removeRefAsset(asset.id)}
                          className="absolute top-1 right-1 h-5 w-5 rounded-full bg-background/90 border border-border flex items-center justify-center hover:bg-background transition-colors"
                          aria-label={`Remove Image ${idx + 1}: ${asset.original_filename ?? asset.filename}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {/* Analyzing overlay */}
                        {analyzing && (
                          <div className="absolute inset-x-0 bottom-0 bg-background/85 px-1.5 py-0.5 flex items-center gap-1 justify-center">
                            <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden="true" />
                            <span className="text-[9px] uppercase tracking-wide">Analyzing</span>
                          </div>
                        )}
                      </div>
                      {/* Label */}
                      <span className="text-[11px] font-medium text-foreground/80 leading-none">
                        Image {idx + 1}
                      </span>
                      {/* Click-to-insert: appends the image's @-tag to the
                          prompt textarea. We keep the inserted text as the
                          machine-readable @ImageN token (script generator +
                          _inject_reference_tags both consume it) but only
                          show the user a friendly "Insert in prompt" label. */}
                      <button
                        type="button"
                        onClick={() => insertImageTag(idx)}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:border-foreground/60 transition-colors"
                        title={`Reference Image ${idx + 1} in your prompt text`}
                      >
                        Insert in prompt
                      </button>
                    </div>
                  )
                })}
                {selectedRefAssets.length < MAX_REF_IMAGES && (
                  <p className="self-center text-[11px] text-muted-foreground">
                    {MAX_REF_IMAGES - selectedRefAssets.length} more slot{MAX_REF_IMAGES - selectedRefAssets.length !== 1 ? 's' : ''} available
                  </p>
                )}
                {selectedRefAssets.length >= MAX_REF_IMAGES && (
                  <p className="self-center text-[11px] text-muted-foreground">
                    Max {MAX_REF_IMAGES} images.
                  </p>
                )}
              </div>
            )}

            {/* Add more images — shown when under limit */}
            {selectedRefAssets.length < MAX_REF_IMAGES && (
              <div className="space-y-2">
                {/* Product KB images — shown when a product with linked assets is selected */}
                {productImages.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[11px] text-muted-foreground">From product KB:</p>
                    <div className="flex flex-wrap gap-2">
                      {productImages.map((a) => {
                        const isSelected = selectedRefAssets.some((s) => s.id === a.id)
                        const atMax = selectedRefAssets.length >= MAX_REF_IMAGES
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => toggleRefAsset(a)}
                            disabled={atMax && !isSelected}
                            className={cn(
                              'relative w-14 h-14 rounded-md overflow-hidden border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground',
                              isSelected
                                ? 'border-foreground ring-1 ring-foreground'
                                : atMax
                                ? 'border-border opacity-40 cursor-not-allowed'
                                : 'border-border hover:border-foreground/50',
                            )}
                            title={a.original_filename ?? a.filename}
                            aria-pressed={isSelected}
                          >
                            <img
                              src={`/uploads/${a.file_path}`}
                              alt={a.original_filename ?? a.filename}
                              className="w-full h-full object-cover"
                            />
                            {isSelected && (
                              <span className="absolute inset-0 flex items-center justify-center bg-foreground/20">
                                <span className="h-5 w-5 rounded-full bg-foreground text-background text-[9px] font-bold flex items-center justify-center">
                                  {selectedRefAssets.findIndex((s) => s.id === a.id) + 1}
                                </span>
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleOpenRefLibrary}
                    aria-expanded={showRefLibrary}
                  >
                    <Image className="h-3.5 w-3.5" />
                    From library
                  </Button>
                  <label className="cursor-pointer">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        {refImageUploading
                          ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading...</>
                          : <><Upload className="h-3.5 w-3.5" /> Upload image{selectedRefAssets.length + 1 < MAX_REF_IMAGES ? 's' : ''}</>
                        }
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={handleRefUpload}
                      disabled={refImageUploading || selectedRefAssets.length >= MAX_REF_IMAGES}
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Inline library picker */}
            {showRefLibrary && (
              <div className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Select reference images
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      ({selectedRefAssets.length}/{MAX_REF_IMAGES} selected)
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowRefLibrary(false)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Done
                  </button>
                </div>

                {libraryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : libraryAssets.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">
                    No images in library.{' '}
                    <a href="/saved-context" target="_blank" rel="noreferrer" className="underline hover:text-foreground">
                      Upload to Saved Context
                    </a>
                  </p>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedFolderId ?? ''}
                        onChange={(e) => setSelectedFolderId(e.target.value ? Number(e.target.value) : null)}
                        className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs flex-1 min-w-0"
                      >
                        <option value="">All folders ({libraryAssets.length})</option>
                        {libraryFolders.map((f) => {
                          const count = libraryAssets.filter((a) => a.folder_id === f.id).length
                          if (count === 0) return null
                          return (
                            <option key={f.id} value={f.id}>
                              {f.name} ({count})
                            </option>
                          )
                        })}
                      </select>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
                        className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs shrink-0"
                      >
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                        <option value="name">A-Z</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-60 overflow-y-auto">
                      {filteredRefAssets.length === 0 ? (
                        <p className="col-span-full text-xs text-muted-foreground py-4 text-center">
                          No images in this folder.
                        </p>
                      ) : filteredRefAssets.map((a) => {
                        const isSelected = selectedRefAssets.some((s) => s.id === a.id)
                        const selIdx = selectedRefAssets.findIndex((s) => s.id === a.id)
                        const atMax = selectedRefAssets.length >= MAX_REF_IMAGES
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => toggleRefAsset(a)}
                            disabled={atMax && !isSelected}
                            className={cn(
                              'relative aspect-square rounded overflow-hidden border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground',
                              isSelected
                                ? 'border-foreground ring-1 ring-foreground'
                                : atMax
                                ? 'border-border opacity-40 cursor-not-allowed'
                                : 'border-border hover:border-foreground/50',
                            )}
                            title={a.original_filename ?? a.filename}
                            aria-label={`${isSelected ? 'Remove' : 'Add'} ${a.original_filename ?? a.filename}`}
                            aria-pressed={isSelected}
                          >
                            <img
                              src={`/uploads/${a.file_path}`}
                              alt={a.original_filename ?? a.filename}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            {isSelected && (
                              <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-foreground text-background text-[9px] font-bold flex items-center justify-center leading-none">
                                {selIdx + 1}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Idea + settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {isAvatar ? 'What should the avatar say?' : 'Ad concept'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="idea">
                {isAvatar ? 'Talking points *' : 'Ad concept *'}
              </Label>
              <HelpHint text="One or two sentences describing your ad's core concept. The AI expands this into a full multi-scene script. Keep it specific — what's the product, who's it for, what's the hook?" />
            </div>
            <PromptAutocompleteTextarea
              id="idea"
              placeholder={
                isAvatar
                  ? 'e.g. Talk about the benefits of our vitamin C serum — anti-aging, brightening, daily use. Excited but authentic tone.'
                  : selectedProduct
                  ? `e.g. Woman picks up ${selectedProduct.name} from a marble countertop at golden hour, applies it, glows.`
                  : 'e.g. Luxury skincare bottle on a marble countertop at golden hour. Woman picks it up, applies serum, and glows.'
              }
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              rows={5}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{idea.length} chars</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label>Total duration</Label>
                <HelpHint text="Total ad length in seconds (must be a multiple of 5). Each scene is exactly 5, 10, or 15 seconds — these are the only values Seedance supports. Changing this recomputes per-scene durations automatically." />
              </div>
              <Select
                value={String(duration)}
                onValueChange={(v) => handleTotalDurationChange(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 15, 20, 25, 30, 45, 60].map((s) => (
                    <SelectItem key={s} value={String(s)}>{s}s</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label>Scenes</Label>
                <HelpHint text="How many scenes the ad splits into. More scenes = more visual variety but more generation cost. 2-4 works best for short-form. Changing this recomputes per-scene durations automatically." />
              </div>
              <Select
                value={String(sceneCount)}
                onValueChange={(v) => handleSceneCountChange(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} scene{n > 1 ? 's' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Per-scene style picker (product + composite only) ── */}
          {(videoType === 'product' || videoType === 'composite') && (
            <div className="rounded-lg border border-border">
              {/* Collapsible header */}
              <button
                type="button"
                onClick={() => setSceneStylesOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-medium select-none"
                aria-expanded={sceneStylesOpen}
              >
                <span className="flex items-center gap-1.5">
                  Style per scene
                  <HelpHint text="Set a visual style for each scene independently. UGC = raw phone footage. Cinematic = premium dolly + rim light. Lifestyle = natural light editorial. Hype Retail = punchy DTC with colored gels. Each scene can have a different look. Change each scene's duration with the 5s / 10s / 15s toggle below the styles." />
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                    sceneStylesOpen && 'rotate-180',
                  )}
                  aria-hidden="true"
                />
              </button>

              {sceneStylesOpen && (
                <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">
                  {/* Scene tab strip */}
                  {sceneCount > 1 && (
                    <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Scene tabs">
                      {Array.from({ length: sceneCount }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          role="tab"
                          aria-selected={activeSceneTab === i}
                          onClick={() => setActiveSceneTab(i)}
                          className={cn(
                            'rounded-md border px-3 py-1 text-xs font-medium transition-colors',
                            activeSceneTab === i
                              ? 'border-foreground bg-foreground text-background'
                              : 'border-border text-muted-foreground hover:border-foreground/50',
                          )}
                        >
                          Scene {i + 1}
                          <span className="ml-1.5 opacity-60 font-normal">
                            {sceneDurations[i] ?? 5}s
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Style buttons for active scene */}
                  <TooltipProvider delayDuration={250}>
                    <div
                      className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                      role="tabpanel"
                      aria-label={`Style for scene ${activeSceneTab + 1}`}
                    >
                      {VIDEO_MODE_CONFIG.map((mode) => {
                        const isActive = (sceneStyles[activeSceneTab] ?? 'cinematic_product') === mode.value
                        return (
                          <Tooltip key={mode.value}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => {
                                  setSceneStyles((prev) => {
                                    const next = [...prev]
                                    next[activeSceneTab] = mode.value
                                    return next
                                  })
                                }}
                                className={cn(
                                  'rounded-md border px-3 py-2.5 text-left transition-colors',
                                  isActive
                                    ? 'border-foreground bg-foreground text-background'
                                    : 'border-border hover:border-foreground/50 text-muted-foreground',
                                )}
                                aria-pressed={isActive}
                              >
                                <span className="flex items-center gap-1.5 text-sm font-medium leading-snug">
                                  <mode.Icon className="h-3.5 w-3.5 shrink-0" />
                                  {mode.label}
                                </span>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                              {mode.tooltip}
                            </TooltipContent>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </TooltipProvider>

                  {/* Per-scene duration toggle */}
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">
                      Scene {activeSceneTab + 1} duration
                    </p>
                    <div className="flex gap-2">
                      {([5, 10, 15] as const).map((dur) => {
                        const isActive = (sceneDurations[activeSceneTab] ?? 5) === dur
                        return (
                          <button
                            key={dur}
                            type="button"
                            onClick={() => handleSceneDurationChange(activeSceneTab, dur)}
                            className={cn(
                              'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                              isActive
                                ? 'border-foreground bg-foreground text-background'
                                : 'border-border text-muted-foreground hover:border-foreground/50',
                            )}
                            aria-pressed={isActive}
                          >
                            {dur}s
                          </button>
                        )
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground/80">
                      Total: {sceneDurations.slice(0, sceneCount).reduce((s, d) => s + d, 0)}s
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Avatar-mode global style picker ── */}
          {videoType === 'avatar' && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>Video style</Label>
                <HelpHint text="Drives the overall look of the talking-head video. UGC = raw phone footage. Cinematic = produced commercial look. Lifestyle = editorial. Hype Retail = punchy DTC." />
              </div>
              <TooltipProvider delayDuration={250}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {VIDEO_MODE_CONFIG.map((mode) => (
                    <Tooltip key={mode.value}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => {
                            videoModeAutoRef.current = false
                            setVideoMode(mode.value)
                          }}
                          className={cn(
                            'rounded-md border px-3 py-2.5 text-left transition-colors',
                            videoMode === mode.value
                              ? 'border-foreground bg-foreground text-background'
                              : 'border-border hover:border-foreground/50 text-muted-foreground',
                          )}
                          aria-pressed={videoMode === mode.value}
                        >
                          <span className="flex items-center gap-1.5 text-sm font-medium leading-snug">
                            <mode.Icon className="h-3.5 w-3.5 shrink-0" />
                            {mode.label}
                          </span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                        {mode.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </TooltipProvider>
            </div>
          )}

          {/* ── Bridge toggle — same person across scenes ── */}
          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium">Same person across scenes</span>
                  <HelpHint
                    text="When ON, every scene anchors to the previous scene's last frame to lock identity, outfit, and setting. Generates scenes sequentially — slower but visually continuous. Off-by-default because most ads work better with independent scenes (different testimonials, B-roll, etc). Identity drift can still happen in illustrated-storyboard mode; for guaranteed same face use a single 15s scene."
                    side="right"
                  />
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {bridgeEnabled
                    ? 'ON — scenes chain via last frame. Slower (sequential), but identity + outfit + setting stay locked across cuts. Reserves one reference slot for the continuity frame, so the product-image limit is 8.'
                    : 'OFF — scenes generate independently in parallel (faster). Best for most ads.'}
                </p>
                {bridgeEnabled && selectedRefAssets.length > MAX_REF_IMAGES && (
                  <p className="text-[11px] text-destructive leading-relaxed">
                    You have {selectedRefAssets.length} reference images. Bridge
                    reserves one slot for the continuity frame — remove{' '}
                    {selectedRefAssets.length - MAX_REF_IMAGES} so at most{' '}
                    {MAX_REF_IMAGES} remain, or turn bridge off.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setBridgeEnabled((v) => !v)}
                className={cn(
                  'shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2',
                  bridgeEnabled ? 'bg-foreground' : 'bg-border',
                )}
                role="switch"
                aria-checked={bridgeEnabled}
                aria-label="Same person across scenes"
              >
                <span
                  className={cn(
                    'inline-block h-3.5 w-3.5 rounded-full bg-background transition-transform',
                    bridgeEnabled ? 'translate-x-4' : 'translate-x-0.5',
                  )}
                />
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label>Script model</Label>
              <HelpHint text="Which AI writes your script. Gemini is fast and cheap; GPT-5 Mini is an OpenAI alternative for when Gemini is rate-limited or you want a second voice. Cost shows per 1M tokens." />
            </div>
            <Select
              value={scriptModel}
              onValueChange={(v) => handleScriptModelChange(v as ScriptProvider)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scriptModels.length === 0 ? (
                  // Fallback while the catalog is loading or unavailable —
                  // mirrors the API defaults so the form stays submittable.
                  <>
                    <SelectItem value="gemini">Gemini (Google)</SelectItem>
                    <SelectItem value="openrouter">GPT-5 Mini (OpenAI)</SelectItem>
                  </>
                ) : (
                  scriptModels.map((m) => (
                    <SelectItem key={m.id} value={m.id} disabled={!m.available}>
                      <span className="flex items-baseline gap-2">
                        <span>{m.label}</span>
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                          ${m.pricing.input_per_1m_usd.toFixed(2)} / ${m.pricing.output_per_1m_usd.toFixed(2)}
                          /M
                        </span>
                        {!m.available && (
                          <span className="text-[10px] text-amber-500">unavailable</span>
                        )}
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Additional notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any specific instructions, brand tone, or visual references..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="resize-none"
            />
          </div>

          {needsAvatar && selectedRefAssets.length === 0 && (
            <p className="text-xs text-amber-500" role="status">
              Select a reference image above to continue.
            </p>
          )}

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <Button
            onClick={handleSubmit}
            disabled={!canGenerate}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isAvatar ? 'Writing avatar script...' : 'Generating script...'}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate Script
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
